import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import countries from "i18n-iso-countries/index.js";
import { flagEmoji } from "@/lib/flag-emoji";
import { formatCurrency } from "@/lib/format";

interface MultiplierRow {
  countryCode: string;
  multiplier: number;
}

/** `clientPrice` je bruto cijena za klijenta (sa PDV-om), ista ona koju server mnozi
 *  koeficijentom zemlje pri prikazu i bookingu. */
export function CountryMultiplierPreview({ clientPrice }: { clientPrice: number }) {
  const { i18n } = useTranslation();
  const locale = i18n.language === "bs" ? "bs" : "en";

  const { data } = useQuery({
    queryKey: ["country-multipliers"],
    queryFn: async () => {
      const res = await fetch("/api/country-multipliers");
      if (!res.ok) return [] as MultiplierRow[];
      return (await res.json()) as MultiplierRow[];
    },
  });

  const rows = useMemo(() => {
    const list = (data ?? [])
      .filter((r) => r.multiplier !== 1)
      .map((r) => ({
        code: r.countryCode,
        multiplier: r.multiplier,
        effective: Math.round(clientPrice * r.multiplier * 100) / 100,
        name:
          (countries.getName(r.countryCode, locale, { select: "official" }) as
            | string
            | undefined) ?? r.countryCode,
      }));
    list.sort((a, b) => a.name.localeCompare(b.name, locale));
    return list;
  }, [data, clientPrice, locale]);

  if (!clientPrice || clientPrice <= 0 || rows.length === 0) return null;

  return (
    <details className="group rounded-lg border border-border/50 p-3">
      <summary className="cursor-pointer text-sm font-medium text-foreground select-none">
        Cijena za klijente po zemljama
        <span className="ml-2 text-xs text-muted-foreground">({rows.length})</span>
      </summary>
      <ul className="mt-3 space-y-1.5 text-sm">
        {rows.map((r) => (
          <li key={r.code} className="flex items-center gap-2">
            <span className="text-base">{flagEmoji(r.code)}</span>
            <span className="flex-1 text-foreground">{r.name}</span>
            <span className="tabular-nums text-muted-foreground">
              {formatCurrency(r.effective)}
            </span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              ×{r.multiplier}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
